import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import { queryClient } from "api/queryClient";
import { showToastMessage } from "common/service/CommonService";
import Reason from "common/utilities/Reason/Reason";
import { useLabTestsService } from "../api/useStudyScreeningTests";
import { medicalTestFields } from "../helper/studytest.constant";
import { ExistingFileType } from "../helper/studytest.types";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  testTitle: string;
  testType: "lab" | "ecg" | "xray";
  onSave: (data: any) => void;
  editData?: any;
  isEditMode?: boolean;
}

const AddMedicalTestModal: React.FC<Props> = ({
  editData,
  isEditMode,
  isOpen,
  toggle,
  testTitle,
  testType,
  onSave,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      testdate: new Date(),
      remarks: "",
      attachment: null,
      testedby: "",
      reportname: "",
    },
  });

  useEffect(() => {
    if (isEditMode && editData && isOpen) {
      setValue("reportname", editData.reportname || editData.testname || "");
      setValue("testedby", editData.testedby || "");
      setValue("remarks", editData.remarks || "");
      setValue("testdate", editData?.testdate ? new Date(editData.testdate) : new Date());

      let files: ExistingFileType[] = [];
      if (editData.attachment instanceof File) {
        const ext = editData.attachment.name.split(".").pop()?.toUpperCase() || "FILE";
        files = [
          {
            attachmentid: editData.id || editData.studylabtestid,
            name: editData.attachment.name,
            type: ext,
            file: editData.attachment as any,
          },
        ];
      } else if (editData.filename || editData.filepath || editData.testname) {
        const name = editData.filename || editData.testname || "File";
        const ext = (editData.fileextension || name.split(".").pop() || "FILE").toUpperCase();
        files = [
          {
            attachmentid: editData.studylabtestid || editData.id,
            name: name,
            type: ext,
            filepath: editData.filepath,
          },
        ];
      }
      setExistingFile(files);
    } else if (!isOpen) {
      reset();
      setExistingFile([]);
    }
  }, [editData, isEditMode, isOpen]);

  const onSubmit = (data: any) => {
    if (testType === "lab") {
      if (isEditMode) {
        setIsReasonOpen(true);
        setPendingData(data);
        return;
      } else {
        InsertMutation.mutate(data, {
          onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ["lab-tests-selectAll"] });
            queryClient.invalidateQueries({ queryKey: ["volunteer-test-count"] });
            queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
            showToastMessage("success", res.message || "Record created successfully");
            handleClose();
          },
          onError: (error: any) => {
            showToastMessage("error", error?.message || error || "Failed to save record");
          },
        });
      }
    } else {
      // ECG / X-Ray (Frontend side handling)
      onSave({
        ...data,
        existingFiles: existingFile,
        hasExistingFile: existingFile.length > 0,
      });
      showToastMessage("success", isEditMode ? `${testTitle} updated successfully` : `${testTitle} added successfully`);
      handleClose();
    }
  };

  const handleUpdateWithReason = ({ reason }: { reason: string }) => {
    if (!pendingData) return;

    UpdateMutation.mutate(
      {
        remarks: pendingData.remarks,
        reportname: pendingData.reportname,
        studylabtestid: editData.studylabtestid,
        attachment: pendingData.attachment,
        updatereason: reason,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["lab-tests-selectAll"] });
          showToastMessage("success", res.message || "Record updated successfully");

          setIsReasonOpen(false);
          setPendingData(null);

          handleClose();
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message || err || "Failed to update record");
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    setExistingFile([]);
    toggle();
  };

  const [existingFile, setExistingFile] = useState<ExistingFileType[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isReasonOpen, setIsReasonOpen] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);

  const { InsertMutation, UpdateMutation } = useLabTestsService();

  const isInsertLoading = InsertMutation.isPending;
  const isUpdateLoading = UpdateMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      centered
      size="lg"
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader className="p-3" toggle={handleClose}>
        {isEditMode ? "Edit" : "Add"} {testTitle} Record
      </ModalHeader>
      <ModalBody className="p-4">
        <CommonForm
          columns={2}
          control={control}
          errors={errors}
          openPreview={(file: File) => {
            setPreviewFile(file);
            setIsPreviewOpen(true);
          }}
          setValue={setValue}
          fields={medicalTestFields(
            testType,
            existingFile,
            setPreviewFile,
            setPreviewUrl,
            setIsPreviewOpen,
            setExistingFile,
          )}
        />
      </ModalBody>

      <ModalFooter className="border-top p-3 bg-light rounded-bottom">
        <Button color="dark" outline onClick={handleClose}>
          Cancel
        </Button>

        <Button
          color="primary"
          outline
          onClick={handleSubmit(onSubmit)}
          disabled={isInsertLoading || isUpdateLoading}
        >
          {isInsertLoading || isUpdateLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Uploading...
            </>
          ) : isEditMode ? (
            "Update"
          ) : (
            "Upload"
          )}
        </Button>
      </ModalFooter>

      <FilePreviewModal
        file={previewFile}
        fileUrl={previewUrl}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewFile(null);
          setPreviewUrl(null);
        }}
      />
      <Reason
        isOpen={isReasonOpen}
        toggle={() => setIsReasonOpen(false)}
        action="Update"
        id={editData?.studylabtestid}
        click={({ reason }) => handleUpdateWithReason({ reason })}
        isLoading={isUpdateLoading}
      />
    </Modal>
  );
};

export default AddMedicalTestModal;
